import React, { useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { validateFileUpload } from '../shared';
import { ChunkedUploader } from '../utils/chunkedUpload';
// import { useAuth } from '../hooks/useAuth';
import { useUploadContext } from '../contexts/UploadContext';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import ComingSoon from '../components/ComingSoon';
import { 
  Upload as UploadIcon, 
  File, 
  X, 
  CheckCircle, 
  AlertCircle,
  Loader2
} from 'lucide-react';

interface UploadProgress {
  vodId: string;
  filename: string;
  progress: number;
  status: 'uploading' | 'processing' | 'ready' | 'error' | 'completed';
  error?: string;
  metadata?: {
    title?: string;
    description?: string;
    tags?: string[];
  };
}

interface UploadMetadata {
  title: string;
  description: string;
  tags: string;
}

export default function Upload() {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [metadata, setMetadata] = useState<UploadMetadata>({
    title: '',
    description: '',
    tags: ''
  });
  // const { user } = useAuth();
  const { addUpload, updateUpload } = useUploadContext();
  const { showSuccess, showError } = useNotificationsContext();
  const queryClient = useQueryClient();

  const handleChunkedUpload = async (file: File, metadata: UploadMetadata) => {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Add to upload context for persistent tracking
    addUpload({
      id: uploadId,
      filename: file.name
    });

    // Add to local state for immediate UI feedback
    const newUpload: UploadProgress = {
      vodId: uploadId,
      filename: file.name,
      progress: 0,
      status: 'uploading',
      metadata: {
        ...metadata,
        tags: metadata.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      }
    };
    setUploads(prev => [...prev, newUpload]);

    try {
      const result = await ChunkedUploader.uploadFile({
        file,
        apiUrl: `${import.meta.env.VITE_API_URL}/api/upload`,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        onProgress: (progress) => {
          // Update upload context
          updateUpload(uploadId, { progress });
          
          // Update local state
          setUploads(prev => prev.map(upload => 
            upload.vodId === uploadId 
              ? { ...upload, progress }
              : upload
          ));
        },
        onError: (error) => {
          console.error('Chunked upload error:', error);
          updateUpload(uploadId, { 
            status: 'error', 
            error: error.message 
          });
          setUploads(prev => prev.map(upload => 
            upload.vodId === uploadId 
              ? { ...upload, status: 'error', error: error.message }
              : upload
          ));
          showError(`Upload failed: ${error.message}`);
        }
      });

      if (result.success) {
        updateUpload(uploadId, { 
          status: 'completed', 
          progress: 100 
        });
        setUploads(prev => prev.map(upload => 
          upload.vodId === uploadId 
            ? { ...upload, status: 'completed', progress: 100 }
            : upload
        ));
        showSuccess(`Successfully uploaded ${file.name}`);
        queryClient.invalidateQueries('vods');
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      updateUpload(uploadId, { 
        status: 'error', 
        error: errorMessage 
      });
      setUploads(prev => prev.map(upload => 
        upload.vodId === uploadId 
          ? { ...upload, status: 'error', error: errorMessage }
          : upload
      ));
      showError(`Upload failed: ${errorMessage}`);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    try {
      validateFileUpload(file.name, file.size, file.type);
      setSelectedFile(file);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Invalid file');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const startUpload = async () => {
    if (!selectedFile || isUploading) return;

    setIsUploading(true);
    try {
      // Use chunked upload instead of the old method
      await handleChunkedUpload(selectedFile, metadata);
      
      // Clear selected file and reset form
      setSelectedFile(null);
      setMetadata({ title: '', description: '', tags: '' });
      
    } catch (error) {
      console.error('Upload error:', error);
      showError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeUpload = (vodId: string) => {
    setUploads(prev => prev.filter(upload => upload.vodId !== vodId));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload VOD</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload your local video files to archive them securely
        </p>
      </div>

      {/* Upload Area */}
      <div className="card">
        <div
          className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            dragActive 
              ? 'border-twitch-500 bg-twitch-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <UploadIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <h3 className="text-lg font-medium text-gray-900">
              {selectedFile ? 'File Selected' : 'Upload a video file'}
            </h3>
            {selectedFile ? (
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-center space-x-2">
                  <File className="h-5 w-5 text-twitch-600" />
                  <span className="font-medium text-gray-900">{selectedFile.name}</span>
                </div>
                <p className="text-sm text-gray-500">
                  {formatFileSize(selectedFile.size)}
                </p>
                <div className="flex justify-center space-x-3 mt-4">
                  <button
                    onClick={startUpload}
                    disabled={isUploading}
                    className="btn-primary"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Starting upload...
                      </>
                    ) : (
                      <>
                        <UploadIcon className="h-4 w-4 mr-2" />
                        Start Upload
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Drag and drop your video file here, or{' '}
                  <label className="text-twitch-600 hover:text-twitch-700 cursor-pointer">
                    browse
                    <input
                      type="file"
                      className="hidden"
                      accept=".mp4,.mkv"
                      onChange={handleFileInput}
                    />
                  </label>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Supports MP4 and MKV files up to 100GB
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Metadata Form - Show when file is selected or uploads are in progress */}
      {(selectedFile || uploads.some(u => u.status === 'uploading' || u.status === 'processing')) && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Video Metadata</h3>
          <p className="text-sm text-gray-500 mb-4">
            Add metadata for your video while it uploads. This information will be saved with your VOD.
          </p>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={metadata.title}
                onChange={(e) => setMetadata(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter a title for your video"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-twitch-500 focus:border-twitch-500"
              />
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                rows={3}
                value={metadata.description}
                onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your video content"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-twitch-500 focus:border-twitch-500"
              />
            </div>
            
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                id="tags"
                value={metadata.tags}
                onChange={(e) => setMetadata(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="gaming, stream, highlights (comma-separated)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-twitch-500 focus:border-twitch-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate tags with commas
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Progress</h3>
          <div className="space-y-4">
            {uploads.map((upload) => (
              <div key={upload.vodId} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <File className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="font-medium text-gray-900">
                        {upload.metadata?.title || upload.filename}
                      </span>
                      {upload.metadata?.title && upload.metadata.title !== upload.filename && (
                        <p className="text-xs text-gray-500">{upload.filename}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {upload.status === 'ready' && (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    )}
                    {upload.status === 'error' && (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                    {(upload.status === 'uploading' || upload.status === 'processing') && (
                      <Loader2 className="h-5 w-5 text-twitch-600 animate-spin" />
                    )}
                    <button
                      onClick={() => removeUpload(upload.vodId)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {upload.status === 'uploading' && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Uploading...</span>
                      <span>{upload.progress}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${upload.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {upload.status === 'processing' && (
                  <div className="text-sm text-gray-500">
                    Processing video and generating preview...
                  </div>
                )}
                
                {upload.status === 'ready' && (
                  <div className="text-sm text-green-600">
                    Upload completed successfully!
                  </div>
                )}
                
                {upload.status === 'error' && (
                  <div className="text-sm text-red-600">
                    {upload.error || 'Upload failed'}
                  </div>
                )}
                
                {/* Show metadata if available */}
                {upload.metadata && (upload.metadata.description || upload.metadata.tags?.length) && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    {upload.metadata.description && (
                      <p className="text-sm text-gray-600 mb-2">{upload.metadata.description}</p>
                    )}
                    {upload.metadata.tags && upload.metadata.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {upload.metadata.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Guidelines */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="text-lg font-medium text-blue-900 mb-3">Upload Guidelines</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <span className="font-medium mr-2">•</span>
            Supported formats: MP4, MKV
          </li>
          <li className="flex items-start">
            <span className="font-medium mr-2">•</span>
            Maximum file size: 100GB per file
          </li>
          <li className="flex items-start">
            <span className="font-medium mr-2">•</span>
            Files are automatically processed to generate previews
          </li>
          <li className="flex items-start">
            <span className="font-medium mr-2">•</span>
            Videos older than 30 days are moved to long-term storage
          </li>
          <li className="flex items-start">
            <span className="font-medium mr-2">•</span>
            Retention period: {' '}
            <span className="font-medium relative">
              1 year (Free) / 2 years (Premium)
              <ComingSoon size="sm" position="top-right" className="ml-2 relative top-0" />
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}